import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BlockStack, Box, Button, InlineStack, Text } from '@shopify/polaris';
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';

import axios from 'axios';
import StoreLocation from '#client/components/StoreLocation';

const createPOReceivingDocument = async ({ poId }) => {
    const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}`,
        { action: 'create-po-receiving-document' },
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data.data;
};

/**
 *
 * @param {string} param.poId - PO ID to be displayed
 * @param {Object} param.parentData - Parent data object containing PO details
 * @param {Function} param.navigate - Function to navigate to a different route
 * @description PurchaseOrderSummary component displays the summary of a purchase order with its details and actions.
 * @returns
 */
const PurchaseOrderSummary = ({ poId, parentData, navigate }) => {
    var queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: () => createPOReceivingDocument({ poId }),
        onSuccess: (data) => {
            if (data?.receivingDocument) {
                // Invalidate the query to refresh the data
                queryClient.invalidateQueries({ queryKey: ['PurchaseOrderDetail', poId] });
                // Navigate to the receiving document page
                navigate(`/purchase-orders/${poId}/receiving-document/${data.receivingDocument.rdId}`, {
                    state: {
                        status: '',
                        poId: parentData.netsuiteId,
                        asnId: parentData.asnNetsuiteId,
                        netSuiteHumanPoNumber: parentData.netSuiteHumanPoNumber,
                    },
                });
            }
        },
    });

    return (
        <>
            <BlockStack>
                <Box>
                    <Text variant="headingLg">{parentData?.netSuiteHumanPoNumber}</Text>
                </Box>
                <Box style={{ marginTop: '0.75rem' }}>
                    <Text as="p" variant="bodyMd">
                        <b>{PURCHASE_ORDER_STRINGS.STATUS}:</b> <CustomBadge status={parentData?.status} />
                    </Text>
                    <Text>
                        <b>{PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_DETAIL.PURCHASE_ORDER_SUMMARY.VENDOR_NAME}:</b> {parentData?.vendorName}
                    </Text>
                    <Text>
                        <b>{PURCHASE_ORDER_STRINGS.DESTINATION_LOCATION}:</b> <StoreLocation locationId={parentData?.destinationLocation} />
                    </Text>
                    <Text>
                        <b>{PURCHASE_ORDER_STRINGS.START_DATE}:</b> {parentData?.startDate}
                    </Text>
                    <Text>
                        <b>{PURCHASE_ORDER_STRINGS.CANCEL_DATE}:</b> {parentData?.cancelDate}
                    </Text>
                    <Text>
                        <b>{PURCHASE_ORDER_STRINGS.EXPECTED_SHIP_DATE}:</b> {parentData?.expectedShipDate}
                    </Text>
                </Box>
            </BlockStack>
            {parentData?.status !== 'Closed' ? (
                <InlineStack align="end">
                    <Button variant="primary" loading={isPending} onClick={() => mutate()}>
                        {PURCHASE_ORDER_STRINGS.RECEIVE}
                    </Button>
                </InlineStack>
            ) : null}
        </>
    );
};

export default PurchaseOrderSummary;
